import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { Box, Typography } from '@mui/material'

import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'

import useTranslations from '../../../i18n/useTranslations'
import useStyles from './styles'

const Chart = ({ id, data }) => {
  const t = useTranslations()
  const classes = useStyles()
  const chartRef = useRef(null)

  useEffect(() => {
    if (!chartRef.current) {
      am4core.useTheme(am4themes_animated)

      // Remove logo
      am4core.addLicense('ch-custom-attribution')

      chartRef.current = am4core.create(id, am4charts.PieChart)

      chartRef.current.data = data

      chartRef.current.innerRadius = am4core.percent(50)

      // Create series
      let series = chartRef.current.series.push(new am4charts.PieSeries())
      series.dataFields.value = 'value'
      series.dataFields.category = 'name'
      series.dataFields.percent = 'percentage'
      series.slices.template.propertyFields.fill = 'color'

      series.slices.template.cornerRadius = 0
      series.slices.template.innerCornerRadius = 0
      series.labels.template.disabled = true

      let slice = series.slices.template
      slice.states.getKey('hover').properties.cornerRadius = 5

      series.ticks.template.disabled = true

      // this creates initial animation
      series.hiddenState.properties.opacity = 1
      series.hiddenState.properties.endAngle = -90
      series.hiddenState.properties.startAngle = -90

      chartRef.current.legend = new am4charts.Legend()
      chartRef.current.legend.position = 'right'
      chartRef.current.legend.valueLabels.template.text = '{percent}%'

      chartRef.current.legend.valueLabels.template.text = ''
      let marker = chartRef.current.legend.markers.template.children.getIndex(0)
      marker.cornerRadius(12, 12, 12, 12)

      // tooltip
      series.slices.template.tooltipHTML =
        '<strong>{value.value} envíos {name}</strong> | {percent}%'

      series.tooltip.background.fillOpacity = 1
      series.tooltip.background.pointerLength = 0
      series.tooltip.getFillFromObject = false
      series.tooltip.background.fill = am4core.color('#DF0024')
      series.tooltip.autoTextColor = false
      series.tooltip.label.padding(5, 8, 4, 8)
      series.tooltip.background.cornerRadius = 4
      series.tooltip.label.fontSize = 12
      series.tooltip.label.fill = am4core.color('#ffffff')

      series.tooltip.background.strokeWidth = 0
      var shadow = series.tooltip.background.filters.getIndex(0)
      shadow.dx = 0
      shadow.dy = 2
      shadow.blur = 0
      shadow.color = am4core.color('#00000033')
    }
  }, [data, id])

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.data = data
    }
  }, [data])

  const totalCount =
    data?.reduce((prev, current) => prev + current.value, 0) ?? 0

  return (
    <Box className={classes.chartWrapper}>
      <div id={id} className={classes.chart} />
      <Box className={classes.chartText}>
        <Typography
          variant="h1"
          className={clsx(classes.title, 'secondaryTitle', 'decorator')}
        >
          {totalCount}
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          {t('home:total-shipments')}
        </Typography>
      </Box>
    </Box>
  )
}

Chart.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      percentage: PropTypes.number,
      color: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          _value: PropTypes.shape({
            r: PropTypes.number.isRequired,
            g: PropTypes.number.isRequired,
            b: PropTypes.number.isRequired,
          }),
        }),
      ]),
    }),
  ),
}

export default Chart
